import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import BrandLogo from '@/components/BrandLogo';
import { ArrowLeft, Palette, Type, Layout, Zap, Check, X } from 'lucide-react';

const DesignSystemPage = () => {
  const navigate = useNavigate();
  const [copiedToken, setCopiedToken] = useState('');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(text);
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const ColorToken = ({ name, value, description }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex items-center space-x-3">
        <div 
          className="w-12 h-12 rounded-lg border border-gray-300" 
          style={{ backgroundColor: value }}
        ></div>
        <div>
          <p className="font-medium text-gray-900">{name}</p>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <code className="px-2 py-1 bg-gray-100 rounded text-sm">{value}</code>
        <button
          onClick={() => copyToClipboard(value)}
          className="p-2 hover:bg-gray-100 rounded"
        >
          {copiedToken === value ? <Check className="w-4 h-4 text-green-600" /> : <Type className="w-4 h-4 text-gray-600" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Palette className="w-6 h-6 text-[#0056B3]" />
              <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>
                Eptral.Hospital Design System Reference
              </h1>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Brand Identity */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Palette className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>A. Brand Identity</h2>
          </div>

          <div className="space-y-6">
            {/* Logo */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Logo Usage</h3>
              <div className="p-6 bg-white border rounded-lg">
                <BrandLogo className="h-16 w-auto object-contain" />
                <p className="text-sm text-gray-600 mt-4">
                  Place the logo with sufficient white space. Minimum clear space: 20px all around.
                </p>
              </div>
            </div>

            {/* Primary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Primary Color Palette</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorToken name="Primary Blue" value="#0056B3" description="Brand blue - main actions" />
                <ColorToken name="Primary Dark" value="#004494" description="Hover states" />
                <ColorToken name="Secondary" value="#F5F6FA" description="Backgrounds" />
                <ColorToken name="Accent" value="#00AEEF" description="Interactive highlights" />
              </div>
            </div>

            {/* Alert Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Alert Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorToken name="Success" value="#2ECC71" description="Success states" />
                <ColorToken name="Error" value="#E63946" description="Error states" />
                <ColorToken name="Warning" value="#F39C12" description="Warning states" />
                <ColorToken name="Info" value="#3498DB" description="Info states" />
              </div>
            </div>

            {/* Typography */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Typography</h3>
              <div className="space-y-3 p-4 bg-white border rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Font Family</p>
                  <p className="text-[18px] font-semibold">Poppins, sans-serif</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-4xl font-bold mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Headings</p>
                    <p className="text-xs text-gray-500">Semi-bold (600)</p>
                  </div>
                  <div>
                    <p className="text-[28px] font-medium mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Subheadings</p>
                    <p className="text-xs text-gray-500">Medium (500)</p>
                  </div>
                  <div>
                    <p className="text-[22px] font-normal mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Body Text</p>
                    <p className="text-xs text-gray-500">Regular (400)</p>
                  </div>
                  <div>
                    <p className="text-xs font-normal mb-1">AA</p>
                    <p className="text-sm text-gray-600">Captions</p>
                    <p className="text-xs text-gray-500">Small, Uppercase</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Form Elements */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Layout className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>C. Form Elements</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Text Input</label>
              <input 
                type="text" 
                placeholder="Enter text here..." 
                className="exlva-input"
              />
              <p className="text-xs text-gray-500 mt-1">Rounded corners, 1px gray border, blue focus state</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Input with Error</label>
              <input 
                type="text" 
                placeholder="Invalid input" 
                className="exlva-input error"
              />
              <p className="exlva-error-text">This field is required</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Disabled Input</label>
              <input 
                type="text" 
                placeholder="Disabled field" 
                className="exlva-input"
                disabled
              />
            </div>
          </div>
        </Card>

        {/* Buttons */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Zap className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>D. Interactive Elements - Buttons</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3">Button Variants</h3>
              <div className="flex flex-wrap gap-3">
                <button className="exlva-btn exlva-btn-primary">Primary Button</button>
                <button className="exlva-btn exlva-btn-secondary">Secondary Button</button>
                <button className="exlva-btn exlva-btn-accent">Accent Button</button>
                <button className="exlva-btn exlva-btn-success">Success Button</button>
                <button className="exlva-btn exlva-btn-error">Error Button</button>
                <button className="exlva-btn exlva-btn-ghost">Ghost Button</button>
                <button className="exlva-btn exlva-btn-primary" disabled>Disabled Button</button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Button Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <button className="exlva-btn exlva-btn-primary exlva-btn-sm">Small Button</button>
                <button className="exlva-btn exlva-btn-primary">Default Button</button>
                <button className="exlva-btn exlva-btn-primary exlva-btn-lg">Large Button</button>
              </div>
            </div>
          </div>
        </Card>

        {/* Alerts */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>E. Feedback & Alerts</h2>
          
          <div className="space-y-4">
            <div className="exlva-alert exlva-alert-success">
              <strong>Success!</strong> Your changes have been saved successfully.
            </div>
            <div className="exlva-alert exlva-alert-error">
              <strong>Error!</strong> Something went wrong. Please try again.
            </div>
            <div className="exlva-alert exlva-alert-warning">
              <strong>Warning!</strong> Your subscription will expire in 3 days.
            </div>
            <div className="exlva-alert exlva-alert-info">
              <strong>Info!</strong> You have 5 new notifications to review.
            </div>
          </div>
        </Card>

        {/* Cards */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>F. Visual Elements - Cards</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="exlva-card">
              <div className="exlva-card-header">
                <h3 className="exlva-card-title">Card Title</h3>
              </div>
              <p className="text-gray-600">
                This is a card with rounded corners and shadow. Hover to see the elevation effect.
              </p>
            </div>
            <div className="exlva-card">
              <h3 className="text-lg font-semibold mb-2">Statistics Card</h3>
              <p className="text-[28px] font-bold" style={{ color: '#0056B3' }}>1,234</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
            <div className="exlva-card">
              <h3 className="text-lg font-semibold mb-2">Action Card</h3>
              <p className="text-gray-600 mb-4">Quick actions available here.</p>
              <button className="exlva-btn exlva-btn-primary exlva-btn-sm">Take Action</button>
            </div>
          </div>
        </Card>

        {/* Responsive Grid */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>B. Layout & Grid System</h2>
          
          <div className="space-y-4">
            <div className="exlva-grid exlva-grid-cols-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="p-4 bg-blue-100 rounded text-center">Col {i}</div>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              12-column responsive grid system. Collapses to single column on mobile.
            </p>
          </div>
        </Card>

        {/* Animations */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>I. Animations & Transitions</h2>
          
          <div className="space-y-4">
            <div className="exlva-fade-in p-4 bg-blue-50 rounded">
              Fade In Animation
            </div>
            <div className="exlva-slide-in-up p-4 bg-green-50 rounded">
              Slide In Up Animation
            </div>
            <div className="flex items-center space-x-2">
              <div className="exlva-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span>Loading Spinner</span>
            </div>
            <p className="text-sm text-gray-600">
              Transitions: 150-300ms with ease-in-out easing for smooth interactions.
            </p>
          </div>
        </Card>

        {/* Footer */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>J. Footer & Legal</h2>
          
          <div className="exlva-footer">
            <div className="mb-4">
              <BrandLogo className="h-8 w-auto object-contain mx-auto" />
            </div>
            <div className="exlva-footer-links">
              <a href="#" className="exlva-footer-link">Terms & Conditions</a>
              <a href="#" className="exlva-footer-link">Privacy Policy</a>
              <a href="#" className="exlva-footer-link">Contact Us</a>
            </div>
            <div className="exlva-footer-content">
              © 2025 Eptral.Hospital. All rights reserved.
            </div>
          </div>
        </Card>

        {/* Accessibility */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>G. Accessibility Standards</h2>
          
          <div className="space-y-3">
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Minimum contrast ratio of 4.5:1 for text and background</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Full keyboard navigation support with visible focus states</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>ARIA labels and semantic HTML elements</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Responsive text scaling across all screen sizes</p>
            </div>
          </div>
        </Card>

        {/* Usage Guide */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Implementation Guide</h2>
          <div className="space-y-3 text-sm">
            <p>
              <strong>CSS Classes:</strong> Use <code className="bg-white px-2 py-1 rounded">exlva-*</code> prefixed classes for design system components.
            </p>
            <p>
              <strong>CSS Variables:</strong> Use <code className="bg-white px-2 py-1 rounded">var(--exlva-primary)</code> for colors in custom CSS.
            </p>
            <p>
              <strong>Import:</strong> The design system is automatically imported via <code className="bg-white px-2 py-1 rounded">index.css</code>.
            </p>
            <p>
              <strong>File:</strong> <code className="bg-white px-2 py-1 rounded">/app/frontend/src/exlva-design-system.css</code>
            </p>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default DesignSystemPage;
